/**
 * googleCalendar.service.ts
 *
 * Per-user Google Calendar sync settings (Settings page). Ports the legacy
 * settings_sync_options.php experience: connect via OAuth, pick a target
 * calendar, and toggle which appointment details are included. The actual push
 * to Google is done server-side by the legacy cron; these endpoints only
 * connect/configure and queue a backfill.
 *
 * All methods use the shared apiClient (Axios instance with the JWT interceptor).
 */
import { apiClient } from './apiClient';

/** The 8 legacy "included details" toggles (users_api_googlecal columns). */
export interface GoogleCalendarFlags {
  showPatientName: boolean;
  showPatientID: boolean;
  showPlanID: boolean;
  showApptName: boolean;
  showApptType: boolean;
  showApptNotes: boolean;
  showResources: boolean;
  showPlanLink: boolean;
}

export interface GoogleCalendarSettings {
  connected: boolean;
  calendarId: string;
  lastSyncedAt: string | null;
  flags: GoogleCalendarFlags;
}

export interface GoogleCalendarOption {
  id: string;
  summary: string;
  primary: boolean;
  accessRole: string;
}

/** Current connection state + saved settings for the logged-in user. */
export async function getGoogleCalendarSettings(): Promise<GoogleCalendarSettings> {
  const res = await apiClient.get<{ data: GoogleCalendarSettings }>('/settings/google-calendar');
  return res.data.data;
}

/** Google consent URL to start the connect flow (redirect the browser to it). */
export async function getGoogleCalendarOAuthUrl(): Promise<string> {
  const res = await apiClient.get<{ data: { authorizeUrl: string } }>('/settings/google-calendar/oauth-url');
  return res.data.data.authorizeUrl;
}

/** The user's Google calendars for the target-calendar dropdown. */
export async function listGoogleCalendars(): Promise<GoogleCalendarOption[]> {
  const res = await apiClient.get<{ data: { calendars: GoogleCalendarOption[] } }>(
    '/settings/google-calendar/calendars',
  );
  return res.data.data.calendars;
}

/** Save the chosen target calendar + detail toggles. */
export async function updateGoogleCalendarSettings(
  payload: { calendarId: string } & Partial<GoogleCalendarFlags>,
): Promise<GoogleCalendarSettings> {
  const res = await apiClient.put<{ data: GoogleCalendarSettings }>('/settings/google-calendar', payload);
  return res.data.data;
}

/** Queue existing upcoming appointments so the sync cron pushes them to Google. */
export async function syncGoogleCalendarNow(): Promise<{ queued: number }> {
  const res = await apiClient.post<{ data: { queued: number } }>('/settings/google-calendar/sync-now');
  return res.data.data;
}

/** Disconnect: revoke at Google and remove the stored connection. */
export async function disconnectGoogleCalendar(): Promise<{ connected: boolean }> {
  const res = await apiClient.delete<{ data: { connected: boolean } }>('/settings/google-calendar');
  return res.data.data;
}
